import { useQuery } from '@tanstack/react-query';

import { FALLBACK_SIDEBAR_RESPONSE } from './constants';
import { getServerUrl } from '../../../utils/getServerUrl';
import { useQueryEntitlements } from '../../../hooks/useQueryEntitlements';
import { useQueryAccountRestriction } from '../../../hooks/useQueryAccountRestriction';
import { useQuerySession } from '../../../hooks/useQuerySession';

import type { SidebarV3 } from './types';
import { useUniversalNavContext } from '../../../hooks';

export const useQuerySidebar = () => {
  const { data: sessionData } = useQuerySession();
  const { bffHostname } = useUniversalNavContext();

  const currentAccountId = sessionData?.currentAccountId?.toString() || '';
  const isStaff = sessionData?.isStaff?.toString() || '';
  const userId = sessionData?.customUserId?.toString() || '';
  const currentRole = sessionData?.currentUserRole ?? '';

  const { data: entitlements } = useQueryEntitlements(
    sessionData?.currentAccountId
  );

  const { data: accountRestrictionData } = useQueryAccountRestriction(
    sessionData?.currentAccountId
  );

  const result = useQuery<SidebarV3.APIResponse>({
    queryKey: [
      'sidebar',
      currentAccountId,
      isStaff,
      userId,
      currentRole,
      entitlements,
      accountRestrictionData,
    ] as const,
    queryFn: async () => {
      const serverUrl = getServerUrl(bffHostname);

      const sidebarUrl = new URL(
        '/api/v3/navigation-data/sidebar',
        serverUrl || window.location.origin
      );

      if (entitlements) {
        sidebarUrl.searchParams.append(
          'is_alerts_center_enabled',
          entitlements.limits.isAlertsCenterEnabled ? 'true' : 'false'
        );
        sidebarUrl.searchParams.append(
          'is_analytics_dashboard_enabled',
          entitlements.limits.isAnalyticsDashboardEnabled ? 'true' : 'false'
        );

        const isApprovalRequestsEnabled =
          entitlements.limits.isApprovalRequestsEnabled &&
          accountRestrictionData?.approval_required;

        sidebarUrl.searchParams.append(
          'is_approval_requests_enabled',
          isApprovalRequestsEnabled ? 'true' : 'false'
        );
      }

      const response = await fetch(sidebarUrl.toString(), {
        credentials: 'include',
        headers: {
          'x-is-staff': isStaff,
          'x-current-account-id': currentAccountId,
          'x-current-role': currentRole,
          'x-customuser-id': userId,
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const json = await response.json();
      return json.data;
    },
    placeholderData: FALLBACK_SIDEBAR_RESPONSE,
  });

  const data =
    result.isLoading || result.isError
      ? FALLBACK_SIDEBAR_RESPONSE
      : result.data;

  return {
    ...result,
    data,
  };
};
